import "../styles/components/manualconnection/ManualConnection.css";
import UserLogoutBar from "./UserLogoutBar";
// import { SERVER_URLS } from "../utils/serverUrls";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Card, CardContent, CardHeader, CardTitle } from "./manualconnection/card";
import { Button } from "./manualconnection/button";
import { Row } from "./manualconnection/row";
import { Col } from "./manualconnection/col";
import { Input } from "./manualconnection/input";
import ListSelect from "./manualconnection/ListSelect";
import { Label } from "./manualconnection/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./manualconnection/tabs";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./manualconnection/select";
import { Checkbox } from "./manualconnection/checkbox";
import { Separator } from "./manualconnection/separator";
import { Loader2, Play, ChevronRight, CircleCheck, X, RotateCw } from "lucide-react";

import { selectUser } from "../store/userSlice";
import { SessionSpec, endSession } from "../utils/sessionEndpoints";
import { SessionCreatedCallback } from "../utils/sessionSetupFlows";

/* === server utils for calling your backend ================================ */
import { api } from "../api_middleware";
import {
  getHttpUrl,
  getEffectiveEnvironment,
  getLocalEnvOverride,
  getForcePortOverride,
} from "../utils/serverUrls";

/* --------------------------------------------------------------------------
 * Types + API models
 * -------------------------------------------------------------------------- */
export type Org = { id: string; name: string; builds: { id: string; name: string }[] };
export type ResLinkParams = { id: string; configType: string; label: string };

export interface APIOrganization {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  is_active?: boolean;
  is_prod_release?: boolean;
  active_deployment?: {
    id: string;
    created_at: string;
    build: {
      id: string;
      build_name: string;
      entries: Array<{
        platform: string;
        repository_type: string;
        key: string;
      }>;
      deleted_at: string | null;
    };
    is_testbed_deployment: boolean;
    min_hosts: number;
    max_hosts: number | null;
  };
  session_launch_policy?: {
    allow_session_launch: boolean;
    supported_login_methods: string[];
  };
  memory_schema_config?: {
    restrict_memory_schemas: boolean;
    allowed_schemas: string[];
  };
}

interface APIResLinkParams {
  id: string;
  launchable_behavior_type: string;
  display_name?: string | null;
}

/* --------------------------------------------------------------------------
 * Local-mode inference
 * -------------------------------------------------------------------------- */
const PUPPET_DEFAULT_PORT = 8001;
const PIXELS_DEFAULT_PORT = 9005;

function isServedLocally(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "::1";
}

// TODO: align these with SERVER_URLS
const SESSION_REMOTE_OPTIONS = [
  "qa",
  "demo",
  "staging",
  "production",
  // team shortcuts
  "eli",
  "colin",
  "scott",
  "andy2",
  "advaith",
  "jake",
  "cesar",
  "colm",
  "sharang",
] as const;
type SessionRemote = (typeof SESSION_REMOTE_OPTIONS)[number];

/* --------------------------------------------------------------------------
 * Helper: base HTTP URL for Pixels service
 *  - If served locally, force local method + pixels default port (or forced port)
 * -------------------------------------------------------------------------- */
function getBaseHttpUrl(): string {
  const env = getEffectiveEnvironment("PRODUCTION");
  const forcedPort = getForcePortOverride() ?? undefined;

  if (isServedLocally()) {
    const localMethod = getLocalEnvOverride() ?? "local";
    // Use pixels default port for general admin/pixels APIs if not forced
    return getHttpUrl(env, localMethod, forcedPort ?? PIXELS_DEFAULT_PORT, /*prevent*/ undefined);
  }

  // Non-local: rely on environment to route correctly
  return getHttpUrl(env, getLocalEnvOverride() ?? undefined, forcedPort, undefined);
}

/* --------------------------------------------------------------------------
 * Real API: GET v1/pixels/organizations
 * -------------------------------------------------------------------------- */
async function fetchOrganizations(): Promise<Org[]> {
  const base = getBaseHttpUrl();
  const resp = await api("v1/pixels/organizations/", "GET", base);

  const list: APIOrganization[] = Array.isArray(resp)
    ? resp
    : Array.isArray(resp?.organizations)
      ? resp.organizations
      : [];

  const mapped: Org[] = list.map((o) => ({
    id: o.id,
    name: o.name,
    builds: o.active_deployment?.build
      ? [{ id: o.active_deployment.build.id, name: o.active_deployment.build.build_name }]
      : [],
  }));

  mapped.sort((a, b) => a.name.localeCompare(b.name));
  return mapped;
}

const RES_LINK_CACHE: Map<string, ResLinkParams[]> = new Map();

async function fetchResLinkParams(orgId: string): Promise<ResLinkParams[]> {
  if (RES_LINK_CACHE.has(orgId)) return RES_LINK_CACHE.get(orgId)!;

  const base = getBaseHttpUrl();
  const resp = await api(`v1/pixels/organizations/experiences/${orgId}`, "GET", base);

  const raw: APIResLinkParams[] = Array.isArray(resp) ? resp : [];
  const mapped: ResLinkParams[] = raw.map((r) => {
    const configType = r.launchable_behavior_type ?? "unknown";
    const displayName = r.display_name ?? "";
    return {
      id: r.id,
      configType,
      label: `${configType}: ${displayName}(${r.id})`,
    };
  });

  RES_LINK_CACHE.set(orgId, mapped);
  return mapped;
}

/* --------------------------------------------------------------------------
 * Defaults/env – mirrors existing manual screen behavior
 * -------------------------------------------------------------------------- */
type Defaults = {
  conversationId: string;
  org: string;
  puppetEnv: string;
  puppetLocalDevMethod: string;
  puppetLocalDevPort?: number;
  pixelsEnv: string;
  pixelsLocalDevMethod: string;
  pixelsLocalDevPort?: number;
  sessionRemote: string;
  testInfra: boolean;
};

const DefaultConfig: Defaults = {
  conversationId: "",
  org: "10ebc3c0-565b-4bf9-ad10-ecfa7b3224e1",
  puppetEnv: "qa",
  puppetLocalDevMethod: "",
  puppetLocalDevPort: undefined,
  pixelsEnv: "qa",
  pixelsLocalDevMethod: "",
  pixelsLocalDevPort: undefined,
  sessionRemote: "qa",
  testInfra: false,
};

const env = import.meta.env;
const envConfig: Partial<Defaults> = {
  conversationId: env.VITE_MANUAL_CONFIG_CONVO_ID || DefaultConfig.conversationId,
  org: env.VITE_MANUAL_CONFIG_ORG_ID || DefaultConfig.org,
  puppetEnv: env.VITE_MANUAL_CONFIG_PUPPET_ENV || DefaultConfig.puppetEnv,
  puppetLocalDevMethod:
    env.VITE_MANUAL_CONFIG_PUPPET_LOCAL_DEV_METHOD || DefaultConfig.puppetLocalDevMethod,
  puppetLocalDevPort: env.VITE_MANUAL_CONFIG_PUPPET_LOCAL_DEV_PORT
    ? Number(env.VITE_MANUAL_CONFIG_PUPPET_LOCAL_DEV_PORT)
    : DefaultConfig.puppetLocalDevPort,
  pixelsEnv: env.VITE_MANUAL_CONFIG_PIXELS_ENV || DefaultConfig.pixelsEnv,
  pixelsLocalDevMethod:
    env.VITE_MANUAL_CONFIG_PIXELS_LOCAL_DEV_METHOD || DefaultConfig.pixelsLocalDevMethod,
  pixelsLocalDevPort: env.VITE_MANUAL_CONFIG_PIXELS_LOCAL_DEV_PORT
    ? Number(env.VITE_MANUAL_CONFIG_PIXELS_LOCAL_DEV_PORT)
    : DefaultConfig.pixelsLocalDevPort,
  sessionRemote: env.VITE_MANUAL_CONFIG_SESSION_REMOTE || DefaultConfig.sessionRemote,
  testInfra: env.VITE_MANUAL_CONFIG_TEST_INFRA === "true" || DefaultConfig.testInfra,
};

const ACTIVE_DEFAULTS: Defaults = { ...DefaultConfig, ...envConfig };

/* --------------------------------------------------------------------------
 * Props
 * -------------------------------------------------------------------------- */
interface ManualWorkflowsProps {
  onConnect: (
    spec: SessionSpec,
    sessionCreatedCallback: SessionCreatedCallback,
    abortSignal?: AbortSignal
  ) => Promise<void> | void;
}

/* --------------------------------------------------------------------------
 * RAW / ADVANCED (existing form, condensed)
 * -------------------------------------------------------------------------- */
function RawAdvancedForm({ onSubmit }: { onSubmit: (spec: SessionSpec) => void }) {
  const user = useSelector(selectUser);

  const [conversationId, setConversationId] = useState(ACTIVE_DEFAULTS.conversationId);
  const [organizationId, setOrganizationId] = useState(ACTIVE_DEFAULTS.org);
  const [sessionRemote, setSessionRemote] = useState(ACTIVE_DEFAULTS.sessionRemote);

  const [puppetEnvironment, setPuppetEnvironment] = useState(ACTIVE_DEFAULTS.puppetEnv);
  const [puppetLocalDevConnectionMethod, setPuppetLocalDevConnectionMethod] = useState(
    ACTIVE_DEFAULTS.puppetLocalDevMethod
  );
  const [puppetPort, setPuppetPort] = useState<number | undefined>(
    ACTIVE_DEFAULTS.puppetLocalDevPort
  );

  const [pixelsEnvironment, setPixelsEnvironment] = useState(ACTIVE_DEFAULTS.pixelsEnv);
  const [pixelsLocalDevConnectionMethod, setPixelsLocalDevConnectionMethod] = useState(
    ACTIVE_DEFAULTS.pixelsLocalDevMethod
  );
  const [pixelsPort, setPixelsPort] = useState<number | undefined>(
    ACTIVE_DEFAULTS.pixelsLocalDevPort
  );

  const [runOnTestInfra, setRunOnTestInfra] = useState(ACTIVE_DEFAULTS.testInfra);
  const [forceColdLaunch, setForceColdLaunch] = useState(false);
  const [specificNodeId, setSpecificNodeId] = useState<string | undefined>();

  const [resLinkParamsId, setResLinkParamsId] = useState<string | undefined>();
  const [resLinkTestingOrganizationId, setResLinkTestingOrganizationId] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (!organizationId && user?.organization_id) setOrganizationId(user.organization_id);
  }, [user]); // eslint-disable-line

  function submit() {
    const spec: SessionSpec = {
      conversationId,
      organizationId: organizationId.trim(),
      sessionRemote: sessionRemote.trim(),
      puppetConfig: {
        environment: puppetEnvironment,
        localDevConnectionMethod: puppetLocalDevConnectionMethod || undefined,
        port: puppetPort,
      },
      pixelsConfig: {
        environment: pixelsEnvironment,
        localDevConnectionMethod: pixelsLocalDevConnectionMethod || undefined,
        port: pixelsPort,
      },
      testInfra: runOnTestInfra,
      forceColdLaunch: forceColdLaunch,
      resLinkParamsId: resLinkParamsId?.trim(),
      resLinkTestingOrganizationId: resLinkTestingOrganizationId?.trim(),
      specificNodeId: runOnTestInfra ? specificNodeId?.trim() : undefined,
      preventEnvOverride: true,
    };
    onSubmit(spec);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Advanced / Raw (YMMV)</CardTitle>
      </CardHeader>
      <CardContent>
        <Col>
          <Label>Conversation ID</Label>
          <Input
            value={conversationId}
            onChange={(e) => setConversationId(e.target.value)}
            placeholder="Conversation ID"
          />
        </Col>
        <Col>
          <Label>Organization ID</Label>
          <Input
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            placeholder="Organization ID"
          />
        </Col>
        <Col>
          <Label>Res Link Params ID</Label>
          <Input
            value={resLinkParamsId || ""}
            onChange={(e) => setResLinkParamsId(e.target.value || undefined)}
            placeholder="Res Link Params ID (optional)"
          />
        </Col>
        <Col>
          <Label>Override Res Link Testing Organization ID</Label>
          <Input
            value={resLinkTestingOrganizationId || ""}
            onChange={(e) => setResLinkTestingOrganizationId(e.target.value || undefined)}
            placeholder="Override Org ID (optional)"
          />
        </Col>

        <Separator />
        <Row>
          <Col>
            <Label>Puppet Environment</Label>
            <Input
              value={puppetEnvironment}
              onChange={(e) => setPuppetEnvironment(e.target.value)}
              placeholder="qa / dev / staging"
            />
          </Col>
          <Col>
            <Label>Session Remote</Label>
            <Input
              value={sessionRemote}
              onChange={(e) => setSessionRemote(e.target.value)}
              placeholder="qa"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Label>Puppet Local Dev Connection Method</Label>
            <Input
              value={puppetLocalDevConnectionMethod}
              onChange={(e) => setPuppetLocalDevConnectionMethod(e.target.value)}
              placeholder="(optional)"
            />
          </Col>
          <Col>
            <Label>Puppet Port</Label>
            <Input
              type="number"
              value={puppetPort ?? ""}
              onChange={(e) => setPuppetPort(e.target.value ? Number(e.target.value) : undefined)}
              placeholder="8001"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Label>Pixels Environment</Label>
            <Input
              value={pixelsEnvironment}
              onChange={(e) => setPixelsEnvironment(e.target.value)}
              placeholder="qa / dev / staging"
            />
          </Col>
          <Col>
            <Label>Pixels Local Dev Connection Method</Label>
            <Input
              value={pixelsLocalDevConnectionMethod}
              onChange={(e) => setPixelsLocalDevConnectionMethod(e.target.value)}
              placeholder="(optional)"
            />
          </Col>
        </Row>
        <Col>
          <Label>Pixels Port</Label>
          <Input
            type="number"
            value={pixelsPort ?? ""}
            onChange={(e) => setPixelsPort(e.target.value ? Number(e.target.value) : undefined)}
            placeholder="9005"
          />
        </Col>

        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8 }}>
          <Checkbox
            id="testInfra"
            checked={!!runOnTestInfra}
            onChange={(e) => setRunOnTestInfra(e.currentTarget.checked)}
          />
          <Label htmlFor="testInfra">Run on test infra</Label>
        </div>
        {runOnTestInfra && (
          <Col>
            <Label>Specific Node ID</Label>
            <Input
              value={specificNodeId || ""}
              onChange={(e) => setSpecificNodeId(e.target.value || undefined)}
              placeholder="asg::i-0628558f218d3a9d0 (e.g.)"
            />
          </Col>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8 }}>
          <Checkbox
            id="forceColdLaunch"
            checked={!!forceColdLaunch}
            onChange={(e) => setForceColdLaunch(e.currentTarget.checked)}
          />
          <Label htmlFor="forceColdLaunch">Force cold launch (debug: evict phantom first)</Label>
        </div>

        <div style={{ paddingTop: 8 }}>
          <Button onClick={submit} style={{ width: "100%" }}>
            <Play style={{ width: 16, height: 16, marginRight: 8 }} /> Connect
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function specWithSessionRemoteBase(
  base: Omit<SessionSpec, "sessionRemote" | "preventEnvOverride" | "puppetConfig" | "pixelsConfig">,
  sessionRemote: string
): SessionSpec {
  // If user selects "local", wire everything to localhost & allow override
  if (sessionRemote === "local") {
    const forcedPort = getForcePortOverride() ?? undefined;
    const localMethod = getLocalEnvOverride() ?? "local";
    return {
      ...base,
      sessionRemote: env.VITE_MANUAL_CONFIG_SESSION_REMOTE || DefaultConfig.sessionRemote,
      preventEnvOverride: false, // <-- allow getHttpUrl to resolve localhost
      puppetConfig: {
        environment: "qa",
        localDevConnectionMethod: localMethod,
        port: forcedPort ?? 8001,
      },
      pixelsConfig: {
        environment: "qa",
        localDevConnectionMethod: localMethod,
        port: forcedPort ?? 9005,
      },
    };
  }

  // Map sessionRemote to puppet environment (uppercase the sessionRemote for the environment)
  // This ensures that when someone sets a different sessionRemote in wizard mode,
  // the puppet server is automatically set to match
  const puppetEnvironment = sessionRemote.toUpperCase();

  // Non-local remotes keep your previous behavior (prevent override)
  return {
    ...base,
    sessionRemote,
    preventEnvOverride: true,
    puppetConfig: { environment: puppetEnvironment },
    pixelsConfig: { environment: puppetEnvironment },
  };
}

enum Step {
  ORG_BUILD_SELECTION = 1,
  PARAMS_SELECTION = 2,
  CONFIRMATION = 3,
}

/* --------------------------------------------------------------------------
 * TEST CANDIDATE BUILD – guided wizard (org → res-link-params)
 * -------------------------------------------------------------------------- */
function TestCandidateWizard({ onSubmit }: { onSubmit: (spec: SessionSpec) => void }) {
  const [step, setStep] = useState<Step>(Step.ORG_BUILD_SELECTION);

  // Step 1 – select organization + build (informational build label)
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [orgIdA, setOrgIdA] = useState<string | undefined>();
  const [buildLabel, setBuildLabel] = useState<string | undefined>();

  // Step 2 – select org for params; then fetch + select res-link-params for that org
  const [orgIdB, setOrgIdB] = useState<string | undefined>();
  const [resLinkOptions, setResLinkOptions] = useState<ResLinkParams[]>([]);
  const [resLinkParamsId, setResLinkParamsId] = useState<string | undefined>();
  const continueBtnRef = useRef<HTMLButtonElement | null>(null);

  // Step 3 -- confirmation + select session remote + submit
  const isLocalHost = (() => {
    if (typeof window === "undefined") return false;
    const h = window.location.hostname;
    return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0" || h.endsWith(".local");
  })();
  const initialRemote: SessionRemote | undefined = !isLocalHost
    ? "qa"
    : (env.VITE_MANUAL_CONFIG_SESSION_REMOTE as SessionRemote) || undefined;

  const [runOnTestInfra, setRunOnTestInfra] = useState(ACTIVE_DEFAULTS.testInfra);
  const [forceColdLaunch, setForceColdLaunch] = useState(false);
  const [specificNodeId, setSpecificNodeId] = useState<string | undefined>();

  // Advanced scheduling constraints
  const [launchDedicatedPhantom, setLaunchDedicatedPhantom] = useState(false);
  const [phantomWarmupDelaySeconds, setPhantomWarmupDelaySeconds] = useState<number>(30);
  // TODO: Backend not ready for force_new_node yet
  // const [forceNewNode, setForceNewNode] = useState(false);
  // const [requiredNodeType, setRequiredNodeType] = useState<string>('AWS_ASG_INSTANCE');

  // NOTE the union with undefined now:
  const [sessionRemoteChoice, setSessionRemoteChoice] = React.useState<SessionRemote | undefined>(
    initialRemote
  );

  // shared
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load organizations
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const o = await fetchOrganizations();
        setOrgs(o);
      } catch (err: any) {
        setError(err?.message ?? "Failed to load organizations");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // When org for B changes, fetch its res-link-params
  useEffect(() => {
    (async () => {
      setResLinkParamsId(undefined);
      setResLinkOptions([]);
      if (!orgIdB) return;
      try {
        setIsLoading(true);
        const r = await fetchResLinkParams(orgIdB);
        setResLinkOptions(r);
      } catch (err: any) {
        setError(err?.message ?? "Failed to load res-link options");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [orgIdB]);

  useEffect(() => {
    if (step === Step.PARAMS_SELECTION && orgIdA && !orgIdB) setOrgIdB(orgIdA);
  }, [step, orgIdA, orgIdB]);

  // when a res-link is picked, snap the continue button into view
  useEffect(() => {
    if (step === Step.PARAMS_SELECTION && resLinkParamsId && continueBtnRef.current) {
      continueBtnRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [step, resLinkParamsId]);

  // Org items for step A (org + active build if present)
  const orgItems = useMemo(
    () =>
      orgs.flatMap((o) =>
        o.builds.length
          ? o.builds.map((b) => ({
            key: `${o.id}::${b.id}`,
            label: `${o.name} — ${b.name}`,
            orgId: o.id,
            build: b.name as string,
          }))
          : [
            {
              key: `${o.id}::none`,
              label: `${o.name}`,
              orgId: o.id,
              build: undefined as string | undefined,
            },
          ]
      ) as Array<{ key: string; label: string; orgId: string; build: string | undefined }>,
    [orgs]
  );

  function next() {
    setStep((s) => (s < 3 ? ((s + 1) as 2 | 3) : s));
  }
  function prev() {
    setStep((s) => (s > 1 ? ((s - 1) as 1 | 2) : s));
  }

  function submit() {
    setError(null);
    if (!orgIdA) return setError("Select an organization (Step A)");
    if (!orgIdB) return setError("Select an organization for params (Step B)");
    if (!resLinkParamsId) return setError("Select a res-link-params (Step B)");
    if (!sessionRemoteChoice) return setError("Choose a session remote (Step C)");

    const overrideTestingOrgId = orgIdA !== orgIdB ? orgIdB : undefined;

    const base = {
      conversationId: "",
      organizationId: orgIdA,
      testInfra: runOnTestInfra,
      forceColdLaunch: forceColdLaunch,
      specificNodeId: runOnTestInfra ? specificNodeId?.trim() : undefined,
      resLinkParamsId,
      resLinkTestingOrganizationId: overrideTestingOrgId,
      launchDedicatedPhantom: runOnTestInfra && launchDedicatedPhantom ? launchDedicatedPhantom : undefined,
      phantomWarmupDelaySeconds: runOnTestInfra && launchDedicatedPhantom ? phantomWarmupDelaySeconds : undefined,
      // TODO: Backend not ready for force_new_node yet
      // forceNewNode: runOnTestInfra && forceNewNode ? forceNewNode : undefined,
      // requiredNodeType: runOnTestInfra && forceNewNode ? requiredNodeType : undefined,
    } as const;

    const spec = specWithSessionRemoteBase(base, sessionRemoteChoice);
    onSubmit(spec);
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Test Candidate Build</CardTitle>
      </CardHeader>
      <CardContent>
        {error && <div className="mc-error">{error}</div>}

        {/* Stepper */}
        <div className="mc-stepper" aria-label="wizard-steps">
          <div className={`mc-step ${step >= Step.ORG_BUILD_SELECTION ? "mc-step--done" : ""}`}>
            <CircleCheck size={14} /> A. Select org + build
          </div>
          <ChevronRight className="mc-step-arrow" size={14} />
          <div className={`mc-step ${step >= Step.PARAMS_SELECTION ? "mc-step--done" : ""}`}>
            <CircleCheck size={14} /> B. Params
          </div>
          <ChevronRight className="mc-step-arrow" size={14} />
          <div className={`mc-step ${step >= Step.CONFIRMATION ? "mc-step--done" : ""}`}>
            <CircleCheck size={14} /> C. Confirmation
          </div>
        </div>

        {step === Step.ORG_BUILD_SELECTION && (
          <div className="space-y-3">
            <Label>Select Organization of the Build You Want to Run</Label>

            <div className="mc-pane">
              <ListSelect
                items={orgItems.map((i) => ({
                  value: i.key,
                  label: i.label,
                }))}
                value={orgItems.find((i) => i.orgId === orgIdA && i.build === buildLabel)?.key}
                onChange={(v) => {
                  const item = orgItems.find((i) => i.key === v);
                  setOrgIdA(item?.orgId);
                  setBuildLabel(item?.build);
                }}
                placeholder={isLoading ? "Loading organizations…" : "Search organizations…"}
                emptyText="No organizations"
              />
            </div>

            {buildLabel && (
              <p className="mc-label" style={{ marginTop: 6 }}>
                Selected build: {buildLabel}
              </p>
            )}

            <div className="mc-wiz-actions">
              <Button variant="outline" onClick={prev} disabled>
                Back
              </Button>
              <Button onClick={next} disabled={!orgIdA}>
                Continue <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === Step.PARAMS_SELECTION && (
          <div className="mc-step-body">
            <Row>
              <Col>
                <Label>Selection Organization for the Res Link Params</Label>
                <div className="mc-pane">
                  <ListSelect
                    items={orgs.map((o) => ({ value: o.id, label: o.name }))}
                    value={orgIdB}
                    onChange={(v) => setOrgIdB(v)}
                    placeholder="Search organizations…"
                    emptyText="No organizations"
                  />
                </div>
              </Col>

              <Col>
                <Label>Res Link Params ID</Label>
                {!orgIdB ? (
                  <div className="mc-pane-placeholder">Pick an organization to load res-links</div>
                ) : (
                  <div className="mc-pane">
                    <ListSelect
                      items={resLinkOptions.map((r) => ({ value: r.id, label: r.label }))}
                      value={resLinkParamsId}
                      onChange={(v) => setResLinkParamsId(v)}
                      placeholder="Search res-links…"
                      emptyText="No res-links for this org"
                    />
                  </div>
                )}
              </Col>
            </Row>

            <div className="mc-wiz-actions">
              <Button variant="outline" onClick={prev}>
                Back
              </Button>
              <Button ref={continueBtnRef} onClick={next} disabled={!orgIdB || !resLinkParamsId}>
                Continue <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === Step.CONFIRMATION && (
          <div>
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: 16 }}>Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mc-col" style={{ gap: 4 }}>
                  <span className="mc-label">Build-Providing Organization:</span>
                  <div className="mc-input" style={{ paddingTop: 8, paddingBottom: 8 }}>
                    {orgIdA}
                  </div>

                  <span className="mc-label" style={{ marginTop: 8 }}>
                    Res Link Params ID:
                  </span>
                  <div className="mc-input" style={{ paddingTop: 8, paddingBottom: 8 }}>
                    {resLinkParamsId}
                  </div>

                  <span className="mc-label" style={{ marginTop: 8 }}>
                    Res-Link Providing Organization:
                  </span>
                  <div className="mc-input" style={{ paddingTop: 8, paddingBottom: 8 }}>
                    {orgIdB}
                  </div>

                  {orgIdA !== orgIdB ? (
                    <div className="mc-label" style={{ color: "#f59e0b", marginTop: 8 }}>
                      Organizations differ — will set{" "}
                      <code>override_testing_organization_id = {orgIdB}</code>
                    </div>
                  ) : (
                    <div className="mc-label" style={{ color: "#22c55e", marginTop: 8 }}>
                      Organizations match — no override needed.
                    </div>
                  )}

                  <span className="mc-label" style={{ marginTop: 8 }}>
                    Session Remote & Puppet Server
                  </span>
                  <div style={{ maxWidth: 340 }}>
                    <Select
                      value={sessionRemoteChoice}
                      onValueChange={(v) => setSessionRemoteChoice(v as SessionRemote)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose remote…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Deployed Environments</SelectLabel>
                          {SESSION_REMOTE_OPTIONS.slice(0, 4).map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Team</SelectLabel>
                          {SESSION_REMOTE_OPTIONS.slice(4).map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8 }}>
                    <Checkbox
                      id="testInfra"
                      checked={!!runOnTestInfra}
                      onChange={(e) => setRunOnTestInfra(e.currentTarget.checked)}
                    />
                    <Label htmlFor="testInfra">Run on test infra</Label>
                  </div>
                  {runOnTestInfra && (
                    <Col>
                      <Label>Specific Node ID</Label>
                      <Input
                        value={specificNodeId || ""}
                        onChange={(e) => setSpecificNodeId(e.target.value || undefined)}
                        placeholder="asg::i-0628558f218d3a9d0 (e.g.)"
                      />
                    </Col>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8 }}>
                    <Checkbox
                      id="forceColdLaunchWizard"
                      checked={!!forceColdLaunch}
                      onChange={(e) => setForceColdLaunch(e.currentTarget.checked)}
                    />
                    <Label htmlFor="forceColdLaunchWizard">Force cold launch (debug: evict phantom first)</Label>
                  </div>

                  {runOnTestInfra && (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8 }}>
                        <Checkbox
                          id="launchDedicatedPhantom"
                          checked={!!launchDedicatedPhantom}
                          onChange={(e) => setLaunchDedicatedPhantom(e.currentTarget.checked)}
                        />
                        <Label htmlFor="launchDedicatedPhantom">Launch dedicated phantom with warmup delay</Label>
                      </div>
                      {launchDedicatedPhantom && (
                        <Col>
                          <Label>Phantom warmup delay (seconds)</Label>
                          <Input
                            type="number"
                            value={phantomWarmupDelaySeconds}
                            onChange={(e) => setPhantomWarmupDelaySeconds(parseInt(e.target.value) || 30)}
                            placeholder="30"
                            min="1"
                          />
                        </Col>
                      )}

                      {/* TODO: Backend not ready for force_new_node yet */}
                      {/* <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 8 }}>
                        <Checkbox
                          id="forceNewNode"
                          checked={!!forceNewNode}
                          onChange={(e) => setForceNewNode(e.currentTarget.checked)}
                        />
                        <Label htmlFor="forceNewNode">Force new node launch</Label>
                      </div>
                      {forceNewNode && (
                        <Col>
                          <Label>Required node type</Label>
                          <select
                            value={requiredNodeType}
                            onChange={(e) => setRequiredNodeType(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                            }}
                          >
                            <option value="AWS_ASG_INSTANCE">AWS ASG Instance</option>
                            <option value="AZURE_VMSS_INSTANCE">Azure VMSS Instance</option>
                            <option value="MOCK_INSTANCE">Mock Instance</option>
                          </select>
                        </Col>
                      )} */}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8 }}>
              <Button variant="outline" onClick={prev}>
                Back
              </Button>
              <Button onClick={submit}>
                <Play style={{ width: 16, height: 16, marginRight: 8 }} /> Start Session
              </Button>
            </div>
          </div>
        )}

        {isLoading && (
          <div
            className="mc-status mc-status--busy"
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Loader2 style={{ width: 16, height: 16 }} /> Loading…
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------
 * MAIN LAUNCHER – tabbed workflows + session lifecycle helpers
 *  - Normalizes spec based on serving URL (local vs remote) BEFORE connecting
 * -------------------------------------------------------------------------- */
const STORAGE_KEY = "manualConnectionConfig";

const ManualConnectionLauncher: React.FC<ManualWorkflowsProps> = ({ onConnect }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionSpec, setSessionSpec] = useState<SessionSpec | undefined>();
  const [sessionId, setSessionId] = useState<string | undefined>();
  const abortRef = useRef<AbortController | null>(null);

  function persist(spec: SessionSpec) {
    try {
      const prev = localStorage.getItem(STORAGE_KEY);
      const next = { ...(prev ? JSON.parse(prev) : {}), ...spec };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* no-op */
    }
  }

  // Central spec normalizer: if served locally, force local mode
  function normalizeForServingEnv(spec: SessionSpec): SessionSpec {
    if (!isServedLocally()) return spec;

    const forcedPort = getForcePortOverride() ?? undefined;
    const localMethod = getLocalEnvOverride() ?? "local";

    return {
      ...spec,
      preventEnvOverride: false,
      puppetConfig: {
        environment: spec.puppetConfig?.environment || "qa",
        localDevConnectionMethod: spec.puppetConfig?.localDevConnectionMethod ?? localMethod,
        port: spec.puppetConfig?.port ?? forcedPort ?? PUPPET_DEFAULT_PORT,
      },
      pixelsConfig: {
        environment: spec.pixelsConfig?.environment || "qa",
        localDevConnectionMethod: spec.pixelsConfig?.localDevConnectionMethod ?? localMethod,
        port: spec.pixelsConfig?.port ?? forcedPort ?? PIXELS_DEFAULT_PORT,
      },
    };
  }

  async function connect(spec: SessionSpec) {
    setIsConnecting(true);
    setError(null);

    const normalized = normalizeForServingEnv(spec);
    persist(normalized);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await onConnect(
        normalized,
        (createdSpec, createdSessionId) => {
          setSessionSpec(createdSpec);
          setSessionId(createdSessionId);
        },
        ctrl.signal
      );
    } catch (e: any) {
      setSessionSpec(undefined);
      setSessionId(undefined);
      setError(
        e?.name === "AbortError" ? "Connection was cancelled" : e?.message || "Failed to connect"
      );
    } finally {
      abortRef.current = null;
      setIsConnecting(false);
    }
  }

  async function cancel() {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (sessionSpec && sessionId) {
      await endSession(sessionSpec, sessionId);
      setSessionSpec(undefined);
      setSessionId(undefined);
    }
  }

  return (
    <div className="mc-root">
      <div className="mc-wrap">
        <div className="mc-header">
          <h1 className="mc-title">PixelStreaming Debug Connection Workflows</h1>
          <div>
            {sessionId ? (
              <Button variant="destructive" onClick={cancel}>
                <X style={{ width: 16, height: 16, marginRight: 8 }} /> Cancel
              </Button>
            ) : null}
          </div>
        </div>

        {error && <div className="mc-error">{error}</div>}

        <Tabs defaultValue="wizard">
          <TabsList>
            <TabsTrigger value="wizard">Test Candidate Build</TabsTrigger>
            <TabsTrigger value="raw">Advanced / Raw</TabsTrigger>
          </TabsList>

          <TabsContent value="wizard">
            <TestCandidateWizard onSubmit={connect} />
          </TabsContent>

          <TabsContent value="raw">
            <RawAdvancedForm onSubmit={connect} />
          </TabsContent>
        </Tabs>

        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8 }}>
          <div className="mc-status">
            {isConnecting ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Loader2 style={{ width: 12, height: 12 }} /> Connecting…
              </span>
            ) : sessionId ? (
              <span
                className="mc-status mc-status--ok"
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <CircleCheck style={{ width: 12, height: 12 }} /> Connected: {sessionId}
              </span>
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <RotateCw style={{ width: 12, height: 12 }} /> Idle
              </span>
            )}
          </div>
        </div>
      </div>
      <UserLogoutBar />
    </div>
  );
};

export default ManualConnectionLauncher;
